import React from 'react';
import { Container, Header, Link } from '@amzn/awsui-components-console';
import { FormProvider, useForm } from 'react-hook-form';
import { CreateApplicationResponse } from '@amzn/snow-data-service-typescript-client';

import { SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import { useAuthContext } from 'src/providers/AuthContext';
import { logger } from 'src/utils/logger';
import useFetchOnUserActionUsingSDS from '../../../../hooks/useFetchOnUserActionUsingSDS';
import { useNavigation } from '../../navigation/hooks/useNavigation';
import { ROUTES } from '../../navigation/types/navigationTypes';
import { useStore } from 'src/stores/store';

import AddApplicationForm from './AddApplicationPageForm';
import {
	ApplicationProfileFormMetadata,
	convertToApplicationProfileData,
	DEFAULT_APPLICATION_DATA,
} from './ApplicationFormGenerationUtils';

interface AddApplicationPageProps {
	onCancel: () => void;
}

const AddApplicationPage: React.FC<AddApplicationPageProps> = ({
	onCancel,
}) => {
	const methods = useForm<ApplicationProfileFormMetadata>({
		defaultValues: DEFAULT_APPLICATION_DATA,
	});

	const { result: createRes, onClickCB: createApp } =
		useFetchOnUserActionUsingSDS<CreateApplicationResponse>();
	const { userName } = useAuthContext();
	const { navigate } = useNavigation();
	const { addNavigationError } = useStore();

	const submitForm = async (data: ApplicationProfileFormMetadata) => {
		const applicationProfileData = convertToApplicationProfileData(
			data,
			userName
		);

		const response = await createApp({
			path: '/application',
			method: 'POST',
			body: { applicationProfileData },
			url: SDS_APIGATEWAY_ENDPOINT,
		});

		if (response?.error) {
			logger.error(`Error creating application:${response.error}`);
			// Error banner is already displayed by the hook's flashBarItems
		} else {
			addNavigationError({
				message: (
					<>
						Application{' '}
						<Link
							variant="primary"
							onFollow={() =>
								navigate(ROUTES.APPLICATIONS, '/applicationManagement')
							}
						>
							{data.applicationName}{' '}
						</Link>{' '}
						created successfully!
					</>
				),
				type: 'success',
				timestamp: Date.now(),
			});

			navigate(ROUTES.APPLICATIONS, '/applicationManagement');
		}
	};

	return (
		<Container header={<Header variant="h2">Add New Application</Header>}>
			<FormProvider {...methods}>
				<AddApplicationForm
					onSubmit={submitForm}
					onCancel={onCancel}
					isLoading={createRes.isLoading}
					flashBarItems={createRes.flashBarItems}
					error={createRes.error}
				/>
			</FormProvider>
		</Container>
	);
};

export default AddApplicationPage;
